import { useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router';
import {
  LayoutGrid,
  FlaskConical,
  Heart,
  FileDown,
  Info,
  Sparkles,
  Star,
  ArrowRight,
  TrendingUp,
  BookOpen,
  FolderHeart,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useFavoriteActions } from '../hooks/useFavoriteActions';
import { useCartActions } from '../hooks/useCartActions';
import { useDashboard, type DashboardProduct } from '../hooks/useDashboard';
import { useRecentStore } from '../stores/useRecentStore';
import { useSettingsStore } from '../stores/useSettingsStore';
import { useProducts } from '../hooks/useProducts';
import type { Product } from '../types/product';
import Badge from '../components/common/Badge';
import MarketingClaimBadge from '../components/products/MarketingClaimBadge';
import { useTranslation } from '../i18n';

const HomePage = () => {
  const { t } = useTranslation();
  const { isLoggedIn, user } = useAuth();
  const { favorites, favoriteGroups } = useFavoriteActions(isLoggedIn);
  const { cart } = useCartActions(isLoggedIn);
  const { recentlyViewed } = useRecentStore();
  const { favoriteGroupsEnabled, showFavoritesOnHome } = useSettingsStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: dashboard } = useDashboard(isLoggedIn);

  const { products, isLoading } = useProducts();

  const newProducts = useMemo(
    () => products.filter((p) => p.isNew),
    [products],
  );
  const newStudyProducts = useMemo(
    () => products.filter((p) => p.hasNewStudy),
    [products],
  );

  const favoriteProducts = favorites
    .map((id) => products.find((p) => String(p.id) === id))
    .filter(Boolean);

  // Use API-based recently viewed if available, fall back to localStorage
  const recentProducts = useMemo<DashboardProduct[]>(() => {
    if (dashboard?.recentlyViewed && dashboard.recentlyViewed.length > 0) {
      return dashboard.recentlyViewed;
    }
    return recentlyViewed
      .map((id) => products.find((p) => String(p.id) === id))
      .filter((p): p is Product => !!p);
  }, [dashboard, recentlyViewed, products]);

  // Use API-based top indications if available, fall back to client-side computation
  const topIndications = useMemo(() => {
    if (dashboard?.topIndications && dashboard.topIndications.length > 0) {
      return dashboard.topIndications.map((ind) => ({
        id: ind.id,
        name: typeof ind.name === 'string' ? ind.name : (ind.name.en ?? Object.values(ind.name)[0] ?? ''),
        count: ind.count,
      }));
    }
    const buckets: Record<number, { id: number; name: string; count: number }> = {};
    for (const product of recentProducts) {
      if (!product) continue;
      for (const ind of product.indications) {
        if (buckets[ind.id]) {
          buckets[ind.id].count++;
        } else {
          buckets[ind.id] = { id: ind.id, name: ind.name, count: 1 };
        }
      }
    }
    return Object.values(buckets)
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
  }, [dashboard, recentProducts]);

  return (
    <div>
      {/* ================================================================= */}
      {/* Hero banner — vivatis.de-inspired gradient                        */}
      {/* ================================================================= */}
      <div
        className="px-4 py-12 sm:py-16"
        style={{
          background: 'linear-gradient(205deg, #F5A161DD 10%, #527656CC 90%)',
        }}
      >
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold text-white drop-shadow-sm">
            {t.home.welcomeBack(user?.name ?? '')}
          </h1>
          <p className="mt-2 text-white/80 text-lg">
            {t.home.whatToDo}
          </p>

          {/* Quick actions & stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {[
              { label: t.home.actions.startWizard, description: t.home.actions.findMatching, icon: FlaskConical, to: '/wizard' },
              { label: t.home.actions.favorites, description: t.home.actions.productsSaved(favorites.length), icon: Heart, to: '/favorites' },
              { label: t.home.actions.meetingCart, description: t.home.actions.productsInCart(cart.length), icon: FileDown, to: '/cart' },
              { label: t.home.actions.browseProducts, description: isLoading ? t.home.actions.loading : t.home.actions.browseProductsCount(products.length), icon: LayoutGrid, to: '/products' },
            ].map((action) => (
              <button
                key={action.to}
                type="button"
                onClick={() => navigate(action.to)}
                className="flex items-center gap-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-2xl px-4 py-3 transition-all duration-200 cursor-pointer text-left"
              >
                <action.icon className="w-5 h-5 shrink-0 text-white/80" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white leading-tight break-words">
                    {action.label}
                  </p>
                  <p className="text-[11px] text-white/70 truncate">
                    {action.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* Your top indications — sand/warm background band                 */}
      {/* ================================================================= */}
      <div className="px-4 py-8 bg-sand-300/30 dark:bg-gray-800/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-semibold text-anthracite-500 dark:text-gray-100 mb-4">
            <TrendingUp className="w-5 h-5 inline-block mr-1.5 text-vivatis-400 -mt-0.5" />
            {t.home.topIndications}
          </h2>

          {topIndications.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {topIndications.map(({ id, name, count }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() =>
                    navigate(`/products?indication=${id}`)
                  }
                  className="inline-flex items-center gap-1.5 rounded-full bg-white dark:bg-gray-800 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-vivatis-50 hover:text-vivatis-700 dark:hover:bg-vivatis-950/30 dark:hover:text-vivatis-300 transition-colors cursor-pointer shadow-sm"
                >
                  {name}
                  <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">
                    {count}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm">
              <Info size={18} className="text-gray-400 dark:text-gray-500 shrink-0" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t.home.topIndicationsEmpty}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ================================================================= */}
      {/* New products                                                       */}
      {/* ================================================================= */}
      {newProducts.length > 0 && (
        <div className="px-4 py-8 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-anthracite-500 dark:text-gray-100 inline-flex items-center gap-1.5">
                <Sparkles className="w-5 h-5 text-vivatis-400" />
                {t.home.newProducts}
                <span className="relative group">
                  <Info className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help" />
                  <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 w-56 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-xs font-normal leading-relaxed px-3 py-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10">
                    {t.home.newProductsTooltip}
                  </span>
                </span>
              </h2>
              <Link
                to="/products?new=1"
                className="text-sm text-vivatis-500 dark:text-vivatis-400 hover:underline inline-flex items-center gap-1"
              >
                {t.home.viewAll}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {newProducts.slice(0, 6).map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => navigate(`/products/${product.id}`, { state: { backgroundLocation: location } })}
                  className="text-left bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-4 hover:scale-[1.02] hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                        {product.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {product.partner?.name}
                      </p>
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-vivatis-100 dark:bg-vivatis-950/30 text-vivatis-600 dark:text-vivatis-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                      <Star className="w-3 h-3" />
                      {t.common.new}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1 mb-2">
                    {product.indications.slice(0, 2).map((ind) => (
                      <Badge key={ind.id} variant="default">
                        {ind.name}
                      </Badge>
                    ))}
                    {product.indications.length > 2 && (
                      <span className="text-[11px] text-gray-400 dark:text-gray-500 self-center">
                        +{product.indications.length - 2}
                      </span>
                    )}
                  </div>

                  {product.marketingClaims.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {product.marketingClaims.map((claim) => (
                        <MarketingClaimBadge key={claim.id} claim={claim} />
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* New studies                                                       */}
      {/* ================================================================= */}
      {newStudyProducts.length > 0 && (
        <div className="px-4 py-8 bg-blue-50/50 dark:bg-blue-950/10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-anthracite-500 dark:text-gray-100 inline-flex items-center gap-1.5">
                <BookOpen className="w-5 h-5 text-blue-500" />
                {t.home.newStudies}
                <span className="relative group">
                  <Info className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help" />
                  <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 w-56 rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-xs font-normal leading-relaxed px-3 py-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10">
                    {t.home.newStudiesTooltip}
                  </span>
                </span>
              </h2>
              <Link
                to="/products?newStudy=1"
                className="text-sm text-vivatis-500 dark:text-vivatis-400 hover:underline inline-flex items-center gap-1"
              >
                {t.home.viewAll}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {newStudyProducts.slice(0, 6).map((product) => (
                <button
                  key={product.id}
                  type="button"
                  onClick={() => navigate(`/products/${product.id}`, { state: { backgroundLocation: location } })}
                  className="text-left bg-white dark:bg-gray-800/50 rounded-2xl p-4 hover:scale-[1.02] hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200 cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                        {product.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {product.partner?.name}
                      </p>
                    </div>
                    <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                      <BookOpen className="w-3 h-3" />
                      {t.home.newStudyBadge}
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {t.home.publishedStudies(product.clinicalStudies)}
                  </p>

                  <div className="flex flex-wrap gap-1">
                    {product.indications.slice(0, 2).map((ind) => (
                      <Badge key={ind.id} variant="default">
                        {ind.name}
                      </Badge>
                    ))}
                    {product.indications.length > 2 && (
                      <span className="text-[11px] text-gray-400 dark:text-gray-500 self-center">
                        +{product.indications.length - 2}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* Favorites (only when enabled in settings)                         */}
      {/* ================================================================= */}
      {showFavoritesOnHome && (
        <div className="px-4 py-8 bg-anthracite-50 dark:bg-gray-800/30">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-semibold text-anthracite-500 dark:text-gray-100">
                <Heart className="w-5 h-5 inline-block mr-1.5 text-vivatis-400 -mt-0.5" />
                {t.home.favoritesTitle}
              </h2>
              {favoriteProducts.length > 0 && (
                <Link
                  to="/favorites"
                  className="text-sm text-vivatis-500 dark:text-vivatis-400 hover:underline inline-flex items-center gap-1"
                >
                  {t.home.viewAll}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>

            {favoriteGroupsEnabled && favoriteGroups.length > 0 ? (
              /* ---- Grouped view ---- */
              <div className="flex flex-wrap gap-4">
                {favoriteGroups.map((group) => (
                  <Link
                    key={group.id}
                    to="/favorites"
                    className="w-56 bg-white dark:bg-gray-800 hover:scale-[1.02] hover:shadow-md transition-all duration-200 rounded-xl p-4 shadow-sm"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <FolderHeart className="w-4 h-4 text-vivatis-500 dark:text-vivatis-400 shrink-0" />
                      <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                        {group.name}
                      </h4>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t.home.productCountLabel((group.favorites ?? []).length)}
                    </p>
                  </Link>
                ))}
              </div>
            ) : favoriteProducts.length > 0 ? (
              /* ---- Flat product view ---- */
              <div className="flex flex-wrap gap-4">
                {favoriteProducts.map((product) => {
                  if (!product) return null;
                  return (
                    <Link
                      key={product.id}
                      to={`/products/${product.id}`}
                      state={{ backgroundLocation: location }}
                      className="w-56 bg-white dark:bg-gray-800 hover:scale-[1.02] hover:shadow-md transition-all duration-200 rounded-xl p-4 shadow-sm"
                    >
                      <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                        {product.name}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                        {product.partner?.name}
                      </p>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm">
                <Info size={18} className="text-gray-400 dark:text-gray-500 shrink-0" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t.home.noFavorites}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* Recently viewed                                                   */}
      {/* ================================================================= */}
      <div className="px-4 py-8 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-semibold text-anthracite-500 dark:text-gray-100 mb-5">
            {t.home.recentlyViewed}
          </h2>

          {recentProducts.length > 0 ? (
            <div className="flex flex-wrap gap-4">
              {recentProducts.map((product) => {
                if (!product) return null;
                return (
                  <Link
                    key={product.id}
                    to={`/products/${product.id}`}
                      state={{ backgroundLocation: location }}
                    className="w-full sm:w-56 bg-white dark:bg-gray-800 hover:scale-[1.02] hover:shadow-md transition-all duration-200 rounded-xl p-4 shadow-sm"
                  >
                    <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                      {product.name}
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                      {product.partner?.name}
                    </p>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm">
              <Info size={18} className="text-gray-400 dark:text-gray-500 shrink-0" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t.home.noRecentlyViewed}{' '}
                <Link
                  to="/products"
                  className="text-vivatis-500 dark:text-vivatis-400 font-medium hover:underline"
                >
                  {t.home.productCatalog}
                </Link>
                !
              </p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default HomePage;
